import { useMemo, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useSesion } from "../lib/sesion";
import { api, ErrorApi } from "../lib/api";
import { Aviso, Campo } from "../componentes/piezas";
import { useIdioma } from "../lib/idioma";
import { leerCola } from "../lib/sinConexion";

export default function Login() {
  const { t } = useIdioma();
  /*
   * Se leen una vez al montar, no en cada repintado: la cola no cambia
   * mientras nadie esta dentro, y leer localStorage en cada tecla de la
   * contraseña no gana nada.
   *
   * Solo las que saldran solas -las que no tienen error-, porque es lo que el
   * aviso promete. Una que el servidor rechazo necesita que alguien le de a
   * reintentar desde dentro, y decir que sale sola seria mentira.
   */
  const sinEnviar = useMemo(() => leerCola().filter((e) => !e.error).length, []);
  const { entrar } = useSesion();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [club, setClub] = useState("");
  const [clubes, setClubes] = useState<{ slug: string; nombre: string }[]>([]);
  const [error, setError] = useState("");
  const [enviando, setEnviando] = useState(false);

  const registro = useQuery({
    queryKey: ["estado-registro"],
    queryFn: () => api<{ abierto: boolean }>("/auth/registro"),
  });

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setError("");
    setEnviando(true);
    try {
      await entrar(email, password, club || undefined);
    } catch (fallo) {
      if (fallo instanceof ErrorApi && fallo.status === 409) {
        const lista = (fallo.detalles as { clubes?: { slug: string; nombre: string }[] })?.clubes ?? [];
        setClubes(lista);
        setError(t("en.variosClubes"));
      } else {
        setError(fallo instanceof Error ? fallo.message : t("en.fallo"));
      }
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="pantalla-login">
      <form className="caja-login" onSubmit={enviar}>
        <div className="fila" style={{ marginBottom: 18 }}>
          <img src="/icono-192.png" alt="asocis" className="logo-marca" />
          <div>
            <h1 style={{ fontSize: "1.25rem" }}>{t("en.titulo")}</h1>
            <div className="suave pequeno">{t("en.subtitulo")}</div>
          </div>
        </div>

        {sinEnviar > 0 && (
          <Aviso tipo="atencion">
            {sinEnviar === 1
              ? t("en.cola.una")
              : t("en.cola.varias", { cuantas: sinEnviar })}
          </Aviso>
        )}

        <Aviso tipo="error">{error}</Aviso>

        <Campo etiqueta={t("en.email")}>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            required
            autoFocus
          />
        </Campo>

        <Campo etiqueta={t("en.contrasena")}>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
        </Campo>

        {clubes.length > 0 && (
          <Campo etiqueta={t("en.club")}>
            <select value={club} onChange={(e) => setClub(e.target.value)} required>
              <option value="">{t("en.eligeClub")}</option>
              {clubes.map((c) => (
                <option key={c.slug} value={c.slug}>
                  {c.nombre}
                </option>
              ))}
            </select>
          </Campo>
        )}

        <button className="boton grande bloque" disabled={enviando} style={{ marginTop: 6 }}>
          {enviando ? t("en.entrando") : t("en.entrar")}
        </button>

        <p className="centrado pequeno" style={{ marginBottom: 8 }}>
          <Link to="/recuperar">{t("en.olvidada")}</Link>
        </p>

        {registro.data?.abierto && (
          <p className="centrado pequeno suave" style={{ marginBottom: 0 }}>
            {t("en.aunNoEsta")} <Link to="/registro">{t("en.darloDeAlta")}</Link>
          </p>
        )}

        {/* Quien llega aquí por un enlace guardado y no sabe qué es esto,
            tiene que poder llegar a la explicación sin borrar la URL a mano. */}
        <p className="centrado pequeno suave" style={{ marginTop: 10, marginBottom: 0 }}>
          <Link to="/">{t("en.queEs")}</Link>
        </p>
      </form>
    </div>
  );
}
